import { useMemo } from "react";
import { useCourses } from "./useCourses";
import { useCompletedLessons } from "./useCompletedLessons";
import { rankCoursesByRecency } from "@/lib/courseRanking";

/**
 * The user's most recently worked-on course — web port of iOS
 * client-native/src/features/courses/useMostRecentCourse.ts (parity).
 *
 * Used as the Home current-course fallback when the user has no explicit
 * selection saved in localStorage (fresh browser / new device / cleared
 * storage). Because it's DERIVED from backend progress (coursesProgress +
 * completedLessons — both already synced and already fetched by the app), it
 * recovers the right course anywhere, with no backend changes. Previously web
 * had no such fallback and collapsed straight to course 1, which hung Home on
 * the loading skeleton.
 *
 * Selection (iOS parity):
 *   1. exclude 100%-complete courses (never resume a finished course),
 *   2. pick the course of the most recently completed lesson
 *      (completedLessons[] is most-recent-first),
 *   3. tie / no completed-lesson data → highest progress,
 *   4. nothing valid → null.
 */
export function useMostRecentCourse() {
  const {
    coursesData,
    coursesProgressData,
    isCoursesLoading,
    isCoursesProgressLoading,
  } = useCourses();
  const { completedLessons, isLoading: isCompletedLessonsLoading } =
    useCompletedLessons();

  const mostRecentCourse = useMemo(() => {
    if (
      isCoursesLoading ||
      isCoursesProgressLoading ||
      !coursesData ||
      !coursesProgressData
    ) {
      return null;
    }

    const coursesWithProgress = coursesData.courses.map((course) => ({
      courseId: course.courseId,
      progress:
        coursesProgressData.courses.find(
          (p) => p.course.courseId === course.courseId,
        )?.progress ?? 0,
    }));

    return rankCoursesByRecency(coursesWithProgress, completedLessons)[0] ?? null;
  }, [
    coursesData,
    coursesProgressData,
    completedLessons,
    isCoursesLoading,
    isCoursesProgressLoading,
  ]);

  return {
    mostRecentCourse,
    isLoading:
      isCoursesLoading || isCoursesProgressLoading || isCompletedLessonsLoading,
  };
}
